/**
 * TschaukoGuideBody — the reusable content of the Realtime-Voice guide.
 *
 * Extracted from TschaukoVoiceOverlay so the same body (portrait row,
 * transcript / SpeciesView slot, error, PTT button) can be rendered by
 * two hosts:
 *   1) <TschaukoVoiceOverlay>  → the drop-up glass sheet over the map
 *   2) <GuidePage>             → the standalone /chat fullscreen page
 *
 * All state comes from <AgentSessionContext>; the body is presentational
 * and framework-agnostic. Sheet-only chrome (drag handle, close button,
 * backdrop, ESC-listener) STAYS in TschaukoVoiceOverlay — it doesn't
 * belong here.
 */

import { Component, lazy, Suspense, useEffect, useRef } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { useAgentSession } from '../lib/voice-agent/AgentSessionContext'
import type { SessionStatus, TranscriptEntry } from '../lib/voice-agent/VoiceAgentController'
import TschaukoPortrait from './TschaukoPortrait'

const SpeciesView = lazy(() => import('./SpeciesView'))

/** Contains any uncaught render error inside SpeciesView so it can't
 *  take down the whole guide (and with it the voice session). */
class SpeciesViewBoundary extends Component<
  { children: ReactNode; onError?: (err: Error) => void },
  { error: string | null }
> {
  state = { error: null as string | null }
  static getDerivedStateFromError(error: Error) {
    return { error: `${error.name}: ${error.message}` }
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[SpeciesViewBoundary] crashed:', error, info.componentStack)
    this.props.onError?.(error)
    const w = window as unknown as {
      __voiceAgentLogInfo?: (e: string, ctx?: Record<string, unknown>) => void
    }
    w.__voiceAgentLogInfo?.('species_view_crash', {
      message: error.message,
      stack: (error.stack || '').slice(0, 500),
    })
  }
  render() {
    if (this.state.error) {
      return (
        <div className="tsch-overlay__empty" style={{ padding: 24 }}>
          Darstellung konnte nicht geladen werden. Bitte zurück und erneut versuchen.
        </div>
      )
    }
    return this.props.children
  }
}

const STATUS_LABEL: Record<SessionStatus, string> = {
  idle: 'Bereit',
  connecting: 'Verbinde …',
  ready: 'Verbunden',
  speaking: 'Dr. Tschauko spricht …',
  listening: 'Ich höre …',
  error: 'Fehler',
  closed: 'Beendet',
}

const PTT_KEYS = new Set([' ', 'Spacebar'])
const PTT_IDLE_LABEL = 'Halten zum Sprechen'
const PTT_ACTIVE_LABEL = '🎤 Ich höre …'

export function TschaukoGuideBody() {
  const { snapshot, controller, hasSpoken } = useAgentSession()
  const transcriptRef = useRef<HTMLDivElement>(null)
  const canTalk = hasSpoken
    || snapshot.status === 'speaking'
    || (snapshot.status === 'ready' && snapshot.entryContext?.opening === 'wait_silently')
  // Standort nur MITLESEN — der Hook abonniert den LocationService passiv und
  // fragt keine Berechtigung an. Ohne Freigabe bleibt er null, dann zeigt die
  // Karte eben nur das Ziel.

  // Spacebar push-to-talk (hold).
  useEffect(() => {
    const isTextField = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
    const down = (e: KeyboardEvent) => {
      if (!canTalk || !PTT_KEYS.has(e.key) || e.repeat || isTextField(e.target)) return
      e.preventDefault()
      controller.setMicActive(true)
    }
    const up = (e: KeyboardEvent) => {
      if (!PTT_KEYS.has(e.key) || isTextField(e.target)) return
      e.preventDefault()
      controller.setMicActive(false)
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [controller, canTalk])

  // Auto-scroll transcript to bottom on append.
  useEffect(() => {
    const el = transcriptRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [snapshot.transcript.length])

  const remainingSec = Math.max(0, 600 - snapshot.elapsedSeconds)
  const mins = String(Math.floor(remainingSec / 60))
  const secs = String(remainingSec % 60).padStart(2, '0')

  return (
    <>
      <div className="tsch-overlay__portrait-row">
        <TschaukoPortrait status={snapshot.status} />
        <div className="tsch-overlay__status">
          <span className="tsch-overlay__status-label">{STATUS_LABEL[snapshot.status]}</span>
          <span className="tsch-overlay__timer">{mins}:{secs}</span>
          {/* Dev-only cost estimate — updates after each bot turn from
           *  response.done usage stats. Hidden in production builds. */}
          {import.meta.env.DEV && snapshot.estimatedCostEur > 0 && (
            <span
              className="tsch-overlay__cost"
              title={`audio_in=${snapshot.tokensUsed.audioInputTokens} audio_out=${snapshot.tokensUsed.audioOutputTokens} text_in=${snapshot.tokensUsed.textInputTokens} text_out=${snapshot.tokensUsed.textOutputTokens}`}
            >
              ≈ {snapshot.estimatedCostEur.toFixed(3)} €
            </span>
          )}
        </div>
      </div>

      {/* Body: inline SpeciesView when the agent has set a knowledge
          focus via show_species, otherwise the live transcript. */}
      {/* Die frühere eingebettete OSM-Kartenkarte für show_location ist
          abgeschaltet (Alex 2026-08-17, Prototyp): Standortfragen führen jetzt
          immer auf die echte App-Karte, siehe TschaukoAgentRoot. */}
      {snapshot.focusedKnowledgeId != null ? (
        <div className="tsch-overlay__knowledge">
          <button
            type="button"
            className="tsch-overlay__knowledge-close"
            onClick={controller.clearFocusedKnowledge}
            aria-label="Zurück zum Chat"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            <span>zurück</span>
          </button>
          <Suspense fallback={<div className="tsch-overlay__empty">Lade Darstellung …</div>}>
            <SpeciesViewBoundary>
              <SpeciesView knowledgeId={snapshot.focusedKnowledgeId} embedded />
            </SpeciesViewBoundary>
          </Suspense>
        </div>
      ) : (
        <div ref={transcriptRef} className="tsch-overlay__transcript" aria-live="polite">
          {!canTalk && snapshot.status !== 'error' && snapshot.status !== 'closed' ? (
            <div className="tsch-overlay__empty" role="status">Dr. Tschauko wird verbunden …</div>
          ) : snapshot.transcript.length === 0 && snapshot.status !== 'connecting' && (
            <div className="tsch-overlay__empty">Stelle eine Frage über die Tscheppaschlucht.</div>
          )}
          {snapshot.transcript.map((entry: TranscriptEntry) => (
            <div
              key={entry.id}
              className={`tsch-bubble tsch-bubble--${entry.speaker}${entry.partial ? ' tsch-bubble--partial' : ''}`}
            >
              {entry.text}
            </div>
          ))}
        </div>
      )}

      {snapshot.errorMessage && (
        <div className="tsch-overlay__error" role="alert">{snapshot.errorMessage}</div>
      )}

      {/* Push-to-talk button. Long-hold semantics need extra browser-
          default suppression beyond CSS user-select:none:
            - onContextMenu blocks the right-click menu (mouse) and the
              long-press menu (touch on Android).
            - onMouseDown preventDefault stops Chrome/Safari from
              starting a text selection when the user drags outside.
            - draggable={false} stops the button from becoming a drag
              source if a child has an inadvertently draggable image. */}
      <button
        type="button"
        draggable={false}
        className={`tsch-overlay__ptt${snapshot.isMicActive ? ' tsch-overlay__ptt--active' : ''}`}
        onContextMenu={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
        onPointerDown={(e) => {
          e.preventDefault()
          try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* noop */ }
          controller.setMicActive(true)
        }}
        onPointerUp={(e) => {
          try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
          controller.setMicActive(false)
        }}
        onPointerCancel={() => controller.setMicActive(false)}
        disabled={!canTalk || snapshot.status === 'error' || snapshot.status === 'closed'}
        aria-pressed={snapshot.isMicActive}
        aria-label="Halten zum Sprechen"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0" />
          <line x1="12" y1="18" x2="12" y2="22" />
        </svg>
        <span>{snapshot.isMicActive ? PTT_ACTIVE_LABEL : PTT_IDLE_LABEL}</span>
      </button>
    </>
  )
}
